// @nexus/shared MCP-projection — the canonical set of projectable MCP tool names.
//
// Implements docs/specs/nexus-mcp-tool-projection-policy-v0.md §3 (a projection
// row's `projected_tool_name` must name a real exposed tool) + §9 (the
// projection validator's deny-by-default-completeness check: a projection whose
// tool name is not a known tool is invalid).
//
// ── Why this lives in @nexus/shared, not in mcp-api ──────────────────────────
// runtime-api owns the admin CRUD for `mcp_tool_projection` (create/update an
// override) and MUST validate that the supplied `tool_name` is a real MCP tool
// — but runtime-api cannot import the mcp-api worker source (separate worker,
// no source dependency). The authoritative source of the tool set remains
// mcp-api's `TOOL_REGISTRY` (apps/mcp-api/src/allowed-tools.ts → MCP_ALLOWED_TOOLS);
// this list is the NEUTRAL projection of that set both workers can import, and
// the `audit:mcp-tool-permissions` CI gate asserts set-equality between this
// list and MCP_ALLOWED_TOOLS so the two CANNOT drift (the same three-way-sync
// discipline RBAC uses — NEXUS CLAUDE.md §9). This is NOT a hardcoded by-case
// fix (FORGE §25): it is a registered, drift-guarded vocabulary, validated as
// DATA, not a literal baked into a code branch.
//
// Adding/removing a tool from the MCP surface (an explicit, audited choice in
// TOOL_REGISTRY) requires the same edit here; the CI gate fails the PR otherwise.

/** The canonical, sorted set of MCP tool names that may be the target of a
 *  per-tenant `mcp_tool_projection` override. Set-equal to mcp-api's
 *  MCP_ALLOWED_TOOLS (asserted by scripts/audit/mcp-tool-permissions-audit.ts). */
export const MCP_PROJECTABLE_TOOL_NAMES: ReadonlySet<string> = new Set<string>([
  // READ
  "get_me",
  "list_my_api_keys",
  "list_users",
  "list_roles",
  "get_role_permissions",
  "list_user_roles",
  "inspect_tenant_state",
  "inspect_tenant_topology",
  "list_clients",
  "get_client",
  "list_sites",
  "get_site",
  "list_buildings",
  "get_building",
  "list_floors",
  "get_floor",
  "list_locations",
  "get_location",
  "list_routes",
  "get_route",
  "list_tasks",
  "get_task",
  "list_task_assignments",
  "get_task_assignment",
  "list_shifts",
  "list_schedule_roster",
  "get_today_overview",
  "list_active_patrol_sessions",
  "list_officers",
  "get_officer",
  "get_officer_enrollment",
  "list_patrol_events",
  "summarize_patrol_activity",
  "get_submission_detail",
  "list_submissions",
  "list_reports",
  "list_notification_logs",
  "list_satellite_captures",
  "get_visual_layers",
  "list_incidents",
  "list_my_incidents",
  "get_incident",
  "get_incident_timeline",
  "list_assets",
  "get_asset",
  "list_notification_rules",
  "list_permissions",
  "list_audit_log",
  "get_tenant",
  "list_user_devices",
  // WRITE
  "create_client",
  "patch_client",
  "create_site",
  "patch_site",
  "create_building",
  "patch_building",
  "create_floor",
  "patch_floor",
  "create_location",
  "patch_location",
  "create_route",
  "patch_route",
  "create_task",
  "patch_task",
  "create_task_assignment",
  "patch_task_assignment",
  "create_shift",
  "patch_shift",
  "create_roster",
  "patch_roster",
  "assign_officer_sites",
  "create_role",
  "update_role",
  "replace_role_permissions",
  "delete_role",
  "set_user_roles",
  "create_incident",
  "create_asset",
  "patch_asset",
  "create_notification_rule",
  "patch_notification_rule",
  "delete_notification_rule",
  "patch_tenant",
  "patch_user_device",
  // EXECUTE
  "capture_satellite_map",
  "assign_incident",
  "escalate_incident",
  "test_notification",
  "issue_pairing_code",
  "unbind_location_tag",
  "rebind_location_tag",
]);

/** True iff `name` is a projectable MCP tool name. */
export function isProjectableToolName(name: string): boolean {
  return MCP_PROJECTABLE_TOOL_NAMES.has(name);
}
